import { Dialog, Transition } from '@headlessui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';

import {
	ChangeTypeEnum,
	EmployeeChangesDataSchema,
	EmployeesChanges,
	ModalDataSchema,
	ShiftDataSchema,
	SituationDataSchema,
} from '@/constants/EmployeesChangesConstants';
import {
	AbsenceChangesColumns,
	AdressChangesColumns,
	ExtraTripChangesColumns,
	MappedChangesColumns,
	PositionChangesModalColumns,
	ShiftChangesColumns,
	SituationChangesColumns,
	StatusChangesColumns,
	VacationChangesColumns,
	WorkStationChangesModalColumns,
} from '@/constants/tableColumns/EmployeeChangesModalColumns';
import {
	AbsenceChangesModal,
	AdressChangesModal,
	ExtraTripChangesModal,
	ExtraTripChangeTableRow,
	MappedChangesModal,
	PositionChangesModal,
	ShiftChangesModal,
	SituationChangesModal,
	StatusChangesModal,
	VacationChangesModal,
	WorkStationChangesModal,
} from '@/types/EmployeeChangesModal';
import { formatddMMyyyy } from '@/utils/formatDate';

import CloseButton from '../atoms/CloseButton';
import Spinner from '../atoms/Spinner';
import SubtitleText from '../atoms/SubtitleText';
import TextComponent from '../atoms/TextComponent';
import ExpandableSubtitle from '../molecules/ExpandableSubtitle';
import Table from './Table';

interface TypeOfChangeProps {
	handleClose: () => void;
	row: EmployeesChanges;
}

type JournalData = z.infer<typeof ModalDataSchema>;

type EmployeeChangeRow =
	| AdressChangesModal
	| StatusChangesModal
	| MappedChangesModal
	| WorkStationChangesModal
	| PositionChangesModal
	| VacationChangesModal
	| AbsenceChangesModal
	| SituationChangesModal
	| ShiftChangesModal
	| ExtraTripChangeTableRow;

function buildSituationChangeRows(
	data: z.infer<typeof EmployeeChangesDataSchema> | null,
): Array<EmployeeChangeRow> {
	if (!data) {
		return [];
	}

	const castData = data as z.infer<typeof SituationDataSchema>;
	const rows = [];

	const trainingDays = castData.situationData?.trainingDays;
	if (!trainingDays) {
		return [];
	}

	for (const [date, schedule] of Object.entries(trainingDays)) {
		rows.push({
			trainingDay: date,
			situation: schedule.value,
		});
	}

	return rows;
}

function buildShiftChangeRows(
	data: z.infer<typeof EmployeeChangesDataSchema> | null,
): Array<EmployeeChangeRow> {
	if (!data) {
		return [];
	}

	const castData = data as z.infer<typeof ShiftDataSchema>;
	const rows = [];

	const trainingDays = castData.shiftData?.trainingDays;
	if (!trainingDays) {
		return [];
	}

	for (const [date, obj] of Object.entries(trainingDays)) {
		rows.push({
			trainingDay: date,
			situation: obj.timelineValue,
			shift: obj.teamStructureShift,
		});
	}

	return rows;
}

function buildExtraTripChangeRows(
	data: ExtraTripChangesModal | null,
	situation: string,
): ExtraTripChangeTableRow[] {
	if (!data?.dates?.length) {
		return [];
	}

	return data.dates.map((date) => ({
		trainingDay: formatddMMyyyy(date),
		situation,
		extraTripSolicitationId: data.extraTripSolicitationId,
	}));
}

export default function TypeOfChange({ handleClose, row }: TypeOfChangeProps) {
	const [isLoading] = useState(false);

	const {
		employeeName,
		employeeNumber,
		changeType,
		beforeChange,
		afterChange,
		training,
	} = row;

	let columns: ColumnDef<EmployeeChangeRow>[] = [];
	let beforeColumns: ColumnDef<EmployeeChangeRow>[] | undefined;
	let beforeRows: JournalData | Array<EmployeeChangeRow>;
	let afterRows: JournalData | Array<EmployeeChangeRow>;

	switch (changeType) {
		case ChangeTypeEnum.ADDRESS:
			columns = AdressChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = beforeChange as AdressChangesModal;
			afterRows = afterChange as AdressChangesModal;
			break;

		case ChangeTypeEnum.FIRED:
			columns = StatusChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = beforeChange as StatusChangesModal;
			afterRows = afterChange as StatusChangesModal;
			break;

		case ChangeTypeEnum.MAPPING:
			columns = MappedChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = {
				...beforeChange,
				trainingName: training,
			} as MappedChangesModal;
			afterRows = {
				...afterChange,
				trainingName: training,
			} as MappedChangesModal;
			break;

		case ChangeTypeEnum.WORKSTATION:
			columns =
				WorkStationChangesModalColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = beforeChange as WorkStationChangesModal;
			afterRows = afterChange as WorkStationChangesModal;
			break;

		case ChangeTypeEnum.POSITION:
			columns = PositionChangesModalColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = beforeChange as PositionChangesModal;
			afterRows = afterChange as PositionChangesModal;
			break;

		// TODO why beforeChange and afterChange are switched?
		case ChangeTypeEnum.VACATION:
			columns = VacationChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = beforeChange as VacationChangesModal;
			afterRows = afterChange as VacationChangesModal;
			break;

		case ChangeTypeEnum.ABSENCE:
			columns = AbsenceChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = (beforeChange as AbsenceChangesModal) ?? [];
			afterRows = (afterChange as AbsenceChangesModal) ?? [];
			break;

		case ChangeTypeEnum.SITUATION:
			columns = SituationChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = buildSituationChangeRows(beforeChange);
			afterRows = buildSituationChangeRows(afterChange);
			break;

		case ChangeTypeEnum.SHIFT:
			columns = ShiftChangesColumns as ColumnDef<EmployeeChangeRow>[];
			beforeRows = buildShiftChangeRows(beforeChange);
			afterRows = buildShiftChangeRows(afterChange);
			break;

		case ChangeTypeEnum.EXTRA_TRIP: {
			const after = afterChange as ExtraTripChangesModal | null;
			beforeColumns =
				ExtraTripChangesColumns() as ColumnDef<EmployeeChangeRow>[];
			columns = ExtraTripChangesColumns(
				row.employeeId,
			) as ColumnDef<EmployeeChangeRow>[];
			beforeRows = buildExtraTripChangeRows(after, 'Sem ida extra');
			afterRows = buildExtraTripChangeRows(after, 'Ida extra marcada');
			break;
		}
	}

	return (
		<Transition appear show={true}>
			<Dialog className="relative z-50" onClose={handleClose}>
				<div className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 px-4 pt-4">
					<Transition.Child
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="custom-scrollbar max-h-[100vh] w-[52rem] flex-col justify-between overflow-auto overflow-y-scroll rounded-lg bg-white">
							<div className="w-full flex-col justify-between px-5 pb-3 pt-5">
								<div className="flex h-auto items-center justify-between">
									<div className="flex justify-between">
										<SubtitleText
											className="text-[20px]"
											subtitle={'Alteração do Colaborador'}
										/>
									</div>
									<CloseButton onClick={handleClose} className="mt-[-1rem]" />
								</div>
								{isLoading && (
									<div className="flex h-72 items-center">
										<Spinner />
									</div>
								)}
								<div className="mt-5">
									<div className="mb-5 flex w-full justify-between">
										<TextComponent
											title={'Nome do Colaborador'}
											width="24rem"
											value={employeeName}
										/>
										<TextComponent
											title={'Chapa do Colaborador'}
											width="24rem"
											value={employeeNumber}
										/>
									</div>
								</div>
								<>
									<ExpandableSubtitle subtitle={'Informação Anterior'}>
										<div className="mb-5">
											<Table
												columns={beforeColumns ?? columns}
												data={
													Array.isArray(beforeRows) ? beforeRows : [beforeRows]
												}
												isWhiteTable={true}
											/>
										</div>
									</ExpandableSubtitle>
									<ExpandableSubtitle subtitle={'Informação Atual'}>
										<div className="mb-5">
											<Table
												columns={columns}
												data={
													Array.isArray(afterRows) ? afterRows : [afterRows]
												}
												isWhiteTable={true}
											/>
										</div>
									</ExpandableSubtitle>
								</>
							</div>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
}
